import React from 'react';
import { useNavigate } from 'react-router-dom';
import createArt from '../assets/create-art.png';
import artmostudio from '../assets/artmostudio.png';
import shareArt from '../assets/share-art.png';
import KidBackground from '../ui/backgrounds/KidBackground';
import splashImage from '../assets/splash.png';
import BackButton from '../components/BackButton';
import { Home, LayoutDashboard, User } from 'lucide-react';

const thumbnails = [
  {
    title: 'Story Boarding',
    image: artmostudio,
    brief: 'Plan your story scenes and visualize your creative journey step by step.'
  },
  {
    title: 'Live Artfully',
    image: shareArt,
    brief: 'Share your art and stories with the world and inspire others to create!'
  }
];

export default function OnceUponAClick() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#FFF8E1] flex justify-center px-4 py-8 md:py-10">
      {/* Amber color splashes background - matching home page */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
        <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
      </div>
      
      {/* Splash images merged into background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-10 right-5 opacity-20"
          style={{ 
            width: '400px',
            height: '200px',
            objectFit: 'cover',
            transform: 'rotate(15deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(2px)'
          }}
        />
        <img 
          src={splashImage} 
          alt="" 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{ 
            width: '300px',
            height: '150px',
            objectFit: 'cover',
            transform: 'translate(-50%, -50%) rotate(10deg)',
            mixBlendMode: 'multiply',
            filter: 'blur(3px)'
          }}
        />
      </div>
      <KidBackground variant="blobs" intensity={1} />

      {/* Window container */}
      <div className="relative z-10 w-full max-w-5xl">
        <div className="bg-white/95 rounded-[32px] shadow-2xl border-2 border-amber-300 p-3 md:p-6">
        {/* Navigation buttons */}
        <div className="flex justify-between items-center mb-4 md:mb-6 gap-1 md:gap-3">
          <BackButton to="/my-art-stories" />
          <div className="flex items-center gap-0.5 md:gap-3">
            <button
              type="button"
              onClick={() => navigate('/my-art-world')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="Artist Corner"
            >
              <LayoutDashboard className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">Artist Corner</span>
            </button>
            <button
              onClick={() => navigate('/home')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="Home"
            >
              <Home className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">Home</span>
            </button>
            <button
              onClick={() => navigate('/artist-wall')}
              className="p-1 md:px-4 md:py-2 bg-white/80 backdrop-blur-sm border border-amber-300/50 text-amber-700 font-medium rounded-full shadow-sm hover:bg-amber-100 transition flex items-center justify-center"
              title="My Gallery"
            >
              <User className="w-2 h-2 md:w-4 md:h-4 flex-shrink-0" />
              <span className="hidden md:inline ml-1 text-[7px] md:text-sm">My Gallery</span>
            </button>
          </div>
        </div>
        <div className="flex flex-col items-center py-8 md:py-12 px-2 w-full">
          <div className="max-w-4xl w-full mx-auto mb-8 md:mb-10 text-center">
            <h1 className="text-3xl md:text-4xl sm:text-5xl font-bold text-slate-900 mb-3 md:mb-4" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>
              Hello fellow storyteller,
            </h1>
            <p className="text-lg md:text-xl sm:text-2xl text-slate-700 mb-6 md:mb-8" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>
              Let's create beautiful stories about you and the world around with Artmosphere.
            </p>
          </div>
          <div className="flex flex-col gap-10 w-full max-w-4xl mx-auto">
            {thumbnails.map((thumb, idx) => (
              <div
                key={idx}
                className="relative flex flex-row items-stretch w-full min-h-[180px] cursor-pointer group hover:scale-[1.02] transition-transform duration-200"
                onClick={() => {
                  if (idx === 0) navigate('/storyboarding-landing');
                  else if (idx === 1) navigate('/live-artfully'); // stub
                }}
                tabIndex={0}
                role="button"
                aria-label={thumb.title}
              >
                <img
                  src={thumb.image}
                  alt={thumb.title}
                  className="w-48 h-48 object-cover rounded-l-2xl border-4 border-amber-200 shadow-lg bg-white flex-shrink-0 group-hover:brightness-105"
                  style={{ background: '#fdf6e3' }}
                />
                <div className="flex-1 flex items-stretch">
                  <div className="flex flex-col justify-center h-full pl-6 md:pl-12 pr-4 md:pr-8 py-4 md:py-6 rounded-r-2xl bg-white/90 backdrop-blur-md border-t-2 md:border-t-4 border-b-2 md:border-b-4 border-r-2 md:border-r-4 border-amber-200 w-full" style={{ minHeight: '100%', background: 'linear-gradient(90deg,rgba(255,255,255,0.9) 40%,rgba(255,248,225,0.9) 100%)' }}>
                    <h2 className="text-lg md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>{thumb.title}</h2>
                    <p className="text-slate-700 text-sm md:text-lg" style={{ fontFamily: '"Baloo 2", "Comic Sans MS", cursive' }}>{thumb.brief}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
} 